import React, { useMemo } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import { Text, TextInput } from '@/components/common/text';
import { useDesktopColors } from '@/constants/residents/desktop/color';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

type DesktopColors = ReturnType<typeof useDesktopColors>;

const buildStyles = (DESKTOP_COLORS: DesktopColors, inputBg: string, inputBorder: string, footerBorder: string) =>
  StyleSheet.create({
    inputGroup: {
      marginBottom: 20,
    },
    label: {
      fontSize: 14,
      fontWeight: '600',
      color: DESKTOP_COLORS.TEXT_PRIMARY,
      marginBottom: 8,
    },
    input: {
      minHeight: 48,
      backgroundColor: inputBg,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: inputBorder,
      paddingHorizontal: 16,
      fontSize: 16,
      color: DESKTOP_COLORS.TEXT_PRIMARY,
    },
    inputError: {
      borderColor: '#EF4444',
    },
    inputWithIcon: {
      position: 'relative',
    },
    inputWithIconPadding: {
      paddingRight: 48,
    },
    inputIcon: {
      position: 'absolute',
      right: 16,
      top: 14,
    },
    errorText: {
      fontSize: 12,
      color: '#EF4444',
      marginTop: 4,
    },
    checkboxContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      marginVertical: 16,
    },
    checkbox: {
      width: 20,
      height: 20,
      borderRadius: 4,
      borderWidth: 2,
      borderColor: DESKTOP_COLORS.TEXT_PRIMARY,
      marginRight: 12,
      alignItems: 'center',
      justifyContent: 'center',
    },
    checkboxChecked: {
      backgroundColor: DESKTOP_COLORS.BUTTON_PRIMARY,
      borderColor: DESKTOP_COLORS.BUTTON_PRIMARY,
    },
    checkboxLabel: {
      fontSize: 14,
      color: DESKTOP_COLORS.TEXT_PRIMARY,
      flex: 1,
    },
    securityNote: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 16,
    },
    securityText: {
      fontSize: 12,
      color: DESKTOP_COLORS.TEXT_TERTIARY,
      marginLeft: 8,
    },
    footer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingTop: 16,
      borderTopWidth: 1,
      borderTopColor: footerBorder,
    },
    footerButton: {
      flex: 0.48,
      minHeight: 44,
      paddingVertical: 12,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
    },
    backButton: {
      borderWidth: 1,
      borderColor: inputBorder,
      backgroundColor: 'transparent',
    },
    backButtonText: {
      fontSize: 16,
      fontWeight: '600',
      color: DESKTOP_COLORS.TEXT_PRIMARY,
    },
    submitButton: {
      backgroundColor: DESKTOP_COLORS.BUTTON_PRIMARY,
    },
    submitButtonDisabled: {
      opacity: 0.5,
    },
    submitButtonText: {
      fontSize: 16,
      fontWeight: '600',
      color: 'white',
    },
    submitButtonTextDisabled: {
      opacity: 0.7,
    },
  });

const useFormStyles = () => {
  const DESKTOP_COLORS = useDesktopColors();
  const { colors } = useThemeColors();
  return useMemo(
    () => buildStyles(DESKTOP_COLORS, colors.activityIcon.bg, colors.card.border, colors.separator),
    [DESKTOP_COLORS, colors.activityIcon.bg, colors.card.border, colors.separator],
  );
};

// Shared Form Input Component
interface PaymentFormInputProps {
  label: string;
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  error?: string;
  keyboardType?: 'default' | 'numeric' | 'email-address';
  maxLength?: number;
  secureTextEntry?: boolean;
  showIcon?: 'check' | 'warning' | 'none';
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';
  style?: any;
}

export const PaymentFormInput: React.FC<PaymentFormInputProps> = ({
  label,
  value,
  onChangeText,
  placeholder,
  error,
  keyboardType = 'default',
  maxLength,
  secureTextEntry = false,
  showIcon = 'none',
  autoCapitalize = 'none',
  style,
}) => {
  const styles = useFormStyles();
  const DESKTOP_COLORS = useDesktopColors();
  const hasIcon = showIcon !== 'none';

  return (
    <View style={[styles.inputGroup, style]}>
      <Text style={styles.label}>{label}</Text>
      <View style={hasIcon ? styles.inputWithIcon : undefined}>
        <TextInput
          style={[
            styles.input,
            hasIcon && styles.inputWithIconPadding,
            error && styles.inputError,
          ]}
          placeholder={placeholder}
          placeholderTextColor={DESKTOP_COLORS.TEXT_TERTIARY}
          value={value}
          onChangeText={onChangeText}
          keyboardType={keyboardType}
          maxLength={maxLength}
          secureTextEntry={secureTextEntry}
          autoCapitalize={autoCapitalize}
        />
        {hasIcon && (
          <View style={styles.inputIcon}>
            {showIcon === 'check' && <CheckIcon />}
            {showIcon === 'warning' && <WarningIcon />}
          </View>
        )}
      </View>
      {error && <Text style={styles.errorText}>{error}</Text>}
    </View>
  );
};

// Shared Checkbox Component
interface PaymentCheckboxProps {
  checked: boolean;
  onToggle: () => void;
  label: string;
  style?: any;
}

export const PaymentCheckbox: React.FC<PaymentCheckboxProps> = ({
  checked,
  onToggle,
  label,
  style,
}) => {
  const styles = useFormStyles();
  return (
    <TouchableOpacity
      style={[styles.checkboxContainer, style]}
      onPress={onToggle}
    >
      <View style={[styles.checkbox, checked && styles.checkboxChecked]}>
        {checked && <CheckIcon size={16} />}
      </View>
      <Text style={styles.checkboxLabel}>{label}</Text>
    </TouchableOpacity>
  );
};

// Shared Security Note Component
export const PaymentSecurityNote: React.FC<{ style?: any }> = ({ style }) => {
  const styles = useFormStyles();
  return (
    <View style={[styles.securityNote, style]}>
      <LockIcon />
      <Text style={styles.securityText}>Your details are secured and encrypted</Text>
    </View>
  );
};

// Shared Form Footer Component
interface PaymentFormFooterProps {
  onBack: () => void;
  onSubmit: () => void;
  backTitle?: string;
  submitTitle?: string;
  submitDisabled?: boolean;
  submitLoading?: boolean;
  style?: any;
}

export const PaymentFormFooter: React.FC<PaymentFormFooterProps> = ({
  onBack,
  onSubmit,
  backTitle = 'Back',
  submitTitle = 'Save',
  submitDisabled = false,
  submitLoading = false,
  style,
}) => {
  const styles = useFormStyles();
  return (
    <View style={[styles.footer, style]}>
      <TouchableOpacity
        style={[styles.footerButton, styles.backButton]}
        onPress={onBack}
      >
        <Text style={styles.backButtonText}>{backTitle}</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[
          styles.footerButton,
          styles.submitButton,
          submitDisabled && styles.submitButtonDisabled,
        ]}
        onPress={onSubmit}
        disabled={submitDisabled || submitLoading}
      >
        <Text style={[styles.submitButtonText, submitDisabled && styles.submitButtonTextDisabled]}>
          {submitLoading ? 'Loading...' : submitTitle}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

// Helper Icons
const CheckIcon: React.FC<{ size?: number }> = ({ size = 20 }) => {
  const Icon = (RESIDENTS_SVG_COMPONENTS as any).CHECK;
  return Icon ? (
    <Icon width={size} height={size} color="#10B981" />
  ) : null;
};

const WarningIcon: React.FC<{ size?: number }> = ({ size = 20 }) => {
  const Icon = (RESIDENTS_SVG_COMPONENTS as any).EXCLAMATION_TRIANGLE;
  return Icon ? (
    <Icon width={size} height={size} color="#EF4444" />
  ) : null;
};

const LockIcon: React.FC = () => {
  const DESKTOP_COLORS = useDesktopColors();
  const Icon = (RESIDENTS_SVG_COMPONENTS as any).PADLOCK;
  return Icon ? (
    <Icon width={16} height={16} color={DESKTOP_COLORS.TEXT_TERTIARY} />
  ) : null;
};

export default PaymentFormInput;
